import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ClipboardCheck, Loader2, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { withAssignment } from './assigned';

const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const Stat = ({ label, value, testId }) => (
    <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 px-3 py-2 text-center">
        <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
        <p className="text-xl font-bold tabular-nums" data-testid={testId}>{value}</p>
    </div>
);

const message = (item) => {
    if (item.submitted) return 'Submitted to your Quality Task. Retakes before the deadline can still raise your best score.';
    if (item.can_submit) return 'You reached the required score. Submit your best score, or retake to improve it.';
    return `You need ${item.required_score}% to submit this task. Retake the simulation to reach it.`;
};

const useOpenItem = (kind, refKey) => {
    const { api } = useAuth();
    const [item, setItem] = useState(refKey ? undefined : null);
    useEffect(() => {
        if (!refKey) { setItem(null); return undefined; }
        let alive = true;
        setItem(undefined);
        api.get('/quality-tasks/my/open-item', { params: { kind, ref_key: refKey } })
            .then(({ data }) => alive && setItem(data.item))
            .catch(() => alive && setItem(null));
        return () => { alive = false; };
    }, [api, kind, refKey]);
    return item;
};

// Result-screen actions for a simulation assigned in a Quality Task; renders children when it isn't one
export const QualityTaskOutcome = ({ kind, refKey, attemptScore, onRetake, retaking = false, children = null }) => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const item = useOpenItem(kind, refKey);
    const [submitting, setSubmitting] = useState(false);

    if (item === undefined) return null;
    if (!item) return children;

    const retake = onRetake || (() => window.location.assign(withAssignment(item.link, item.task_id)));
    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/quality-tasks/my/${item.task_id}/items/${item.item_id}/submit`);
            window.dispatchEvent(new Event('quality-tasks-updated'));
            toast.success(`Submitted to your Quality Task · best score ${data.item.best_score}%`);
            navigate('/quality-tasks');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
            setSubmitting(false);
        }
    };
    const mustRetake = !item.submitted && !item.can_submit;

    return (
        <div className="space-y-4 rounded-2xl border border-violet-500/30 bg-violet-500/10 p-5" data-testid="quality-outcome">
            <div className="flex items-start gap-3">
                <div className="rounded-lg bg-violet-500/20 p-2 text-violet-500"><ClipboardCheck className="h-5 w-5" /></div>
                <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-violet-500">Quality Task</p>
                    <p className="font-semibold leading-snug" data-testid="quality-outcome-task">{item.task_title}</p>
                    <p className="text-xs opacity-70">Assigned by {item.assigned_by_name}</p>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <Stat label="This attempt" value={pct(attemptScore ?? item.last_score)} testId="quality-outcome-attempt" />
                <Stat label="Best score" value={pct(item.best_score)} testId="quality-outcome-best" />
                <Stat label="Required" value={pct(item.required_score)} testId="quality-outcome-required" />
            </div>
            <p className={`text-sm ${mustRetake ? 'font-medium text-amber-600 dark:text-amber-400' : 'opacity-80'}`} data-testid="quality-outcome-message">{message(item)}</p>
            <div className="flex flex-col gap-2 sm:flex-row">
                <Button onClick={retake} disabled={retaking || submitting} variant={mustRetake ? 'default' : 'outline'}
                    className={mustRetake ? 'flex-1 bg-violet-600 text-white hover:bg-violet-700' : 'flex-1 text-foreground'} data-testid="quality-outcome-retake-btn">
                    {retaking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                    {item.submitted || item.can_submit ? 'Retake to improve' : 'Retake'}
                </Button>
                {item.can_submit && (
                    <Button onClick={submit} disabled={submitting || retaking} className="flex-1 bg-violet-600 text-white hover:bg-violet-700" data-testid="quality-outcome-submit-btn">
                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                        Submit best score ({item.best_score}%)
                    </Button>
                )}
                {item.submitted && (
                    <Button onClick={() => navigate('/quality-tasks')} className="flex-1" data-testid="quality-outcome-back-btn">
                        Back to Quality Tasks <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
};
